// Starter house ads with CC0 / public-domain photos (client/public/ads, credits in CREDITS.md).
// Shown when no ads have been set up at /admin, which can also import these (photos included).
// `scene` is the gradient shown while a photo loads or if an ad has none.

export interface HouseAd {
  id: string;
  advertiser: string;
  headline: string;
  body: string;
  cta: string;
  url: string;
  image?: string | null;
  imageBrief?: string;
  scene: string;
}

/**
 * Square logo shown in the middle of an ad's QR code, matched by advertiser name or link.
 * Add a file to /public/ads/logos and a line here for a new advertiser.
 */
const ADVERTISER_LOGOS: Array<{ match: RegExp; logo: string }> = [
  { match: /kirkpatrick consult|kcltd\.es/i, logo: '/ads/logos/kcl.png' },
  { match: /el tiquetero|eltiquetero\.com/i, logo: '/ads/logos/el-tiquetero.png' },
];

export function logoFor(ad: Pick<HouseAd, 'advertiser' | 'url'>): string | null {
  return ADVERTISER_LOGOS.find((l) => l.match.test(ad.advertiser) || l.match.test(ad.url))?.logo ?? null;
}

const KCL_URL = 'https://kcltd.es/?utm_source=kcl-canvas&utm_medium=house-ad';
const TIQ_URL = 'https://eltiquetero.com/?utm_source=kcl-canvas&utm_medium=house-ad';

const KCL = 'Kirkpatrick Consult Limited';
const TIQ = 'El Tiquetero';

const g = (from: string, mid: string, to: string, angle = 180) => `linear-gradient(${angle}deg, ${from} 0%, ${mid} 55%, ${to} 100%)`;

export const HOUSE_ADS: HouseAd[] = [
  // ---- Kirkpatrick Consult Limited – fractional IT & people solutions (kcltd.es) -------
  {
    id: 'kcl-mountain',
    advertiser: KCL,
    headline: 'This mountain took millions of years to build.',
    body: 'Your IT shouldn’t. Fractional IT from KCL – big-company know-how, without the big-company bill.',
    cta: 'Talk to KCL',
    url: KCL_URL,
    image: '/ads/kcl-mountain.jpg',
    imageBrief: 'Alpine lake reflecting pink-lit peaks.',
    scene: g('#1e3a8a', '#64748b', '#e2e8f0'),
  },
  {
    id: 'kcl-bees',
    advertiser: KCL,
    headline: 'Bees don’t raise IT tickets.',
    body: 'Your team does. KCL IT Support answers them – quickly, and in plain English.',
    cta: 'Get IT support',
    url: KCL_URL,
    image: '/ads/kcl-bees.jpg',
    imageBrief: 'Honeybee landing on a yellow flower.',
    scene: g('#451a03', '#d97706', '#fde68a', 160),
  },
  {
    id: 'kcl-clouds',
    advertiser: KCL,
    headline: 'Not all clouds are created equal.',
    body: 'Microsoft, Google or something else entirely – platform-neutral cloud advice that fits your business.',
    cta: 'Find your cloud',
    url: KCL_URL,
    image: '/ads/kcl-clouds.jpg',
    imageBrief: 'Towering white cumulus cloud in a blue sky.',
    scene: g('#0c4a6e', '#38bdf8', '#f0f9ff'),
  },
  {
    id: 'kcl-owl',
    advertiser: KCL,
    headline: 'Owls see what others miss.',
    body: 'So does a KCL systems audit – finding the hidden steps that cost you time and money.',
    cta: 'Book an audit',
    url: KCL_URL,
    image: '/ads/kcl-owl.jpg',
    imageBrief: 'Boreal owl watching from a branch.',
    scene: 'radial-gradient(circle at 75% 25%, #fef3c7 0 6%, transparent 7%), linear-gradient(180deg, #020617 0%, #1e1b4b 100%)',
  },
  {
    id: 'kcl-river',
    advertiser: KCL,
    headline: 'Water always finds the easiest way.',
    body: 'Your work should too. Power Automate and smart integrations that make everyday tasks flow.',
    cta: 'Automate it',
    url: KCL_URL,
    image: '/ads/kcl-river.jpg',
    imageBrief: 'Waterfall in a mossy green forest.',
    scene: g('#082f49', '#0369a1', '#7dd3fc'),
  },
  {
    id: 'kcl-tortoise',
    advertiser: KCL,
    headline: 'Slow and steady wins the race.',
    body: 'Not in Excel it doesn’t. Training from the team trusted by household names – one-to-one, classroom or online.',
    cta: 'Book training',
    url: KCL_URL,
    image: '/ads/kcl-tortoise.jpg',
    imageBrief: 'Giant tortoise in long grass.',
    scene: g('#1a2e05', '#4d7c0f', '#d9f99d', 170),
  },
  {
    id: 'kcl-geese',
    advertiser: KCL,
    headline: 'Geese fly thousands of miles without a single “quick sync”.',
    body: 'Great teams just work together. KCL Talent Optimisation shows you how yours really ticks.',
    cta: 'Optimise your team',
    url: KCL_URL,
    image: '/ads/kcl-geese.jpg',
    imageBrief: 'Flock of geese migrating across a moody sky.',
    scene: g('#7c2d12', '#ea580c', '#fed7aa'),
  },
  {
    id: 'kcl-wolves',
    advertiser: KCL,
    headline: 'Wolves know exactly who does what in the pack.',
    body: 'Do you? The Predictive Index helps you hire and build teams that fit.',
    cta: 'Discover PI',
    url: KCL_URL,
    image: '/ads/kcl-wolves.jpg',
    imageBrief: 'Grey wolf resting in long grass.',
    scene: g('#1c1917', '#57534e', '#d6d3d1'),
  },
  {
    id: 'kcl-oak',
    advertiser: KCL,
    headline: 'Every mighty oak was once a nut that stood its ground.',
    body: 'Leadership coaching from KCL – grow your confidence, one session at a time.',
    cta: 'Start coaching',
    url: KCL_URL,
    image: '/ads/kcl-oak.jpg',
    imageBrief: 'Solitary tree in a green meadow.',
    scene: g('#052e16', '#15803d', '#bbf7d0', 170),
  },
  {
    id: 'kcl-sunflowers',
    advertiser: KCL,
    headline: 'Sunflowers always turn towards the sun.',
    body: 'Business stalled? KCL business development helps you turn back towards growth.',
    cta: 'Grow your business',
    url: KCL_URL,
    image: '/ads/kcl-sunflowers.jpg',
    imageBrief: 'Tall sunflower above a sunny field of sunflowers.',
    scene: g('#451a03', '#eab308', '#fef9c3', 160),
  },
  {
    id: 'kcl-peacock',
    advertiser: KCL,
    headline: 'The peacock nailed its branding millions of years ago.',
    body: 'Your turn. Logos, websites and print design by KCL.',
    cta: 'Brand me',
    url: KCL_URL,
    image: '/ads/kcl-peacock.jpg',
    imageBrief: 'Peacock with its tail fully fanned.',
    scene: g('#064e3b', '#10b981', '#a7f3d0', 150),
  },
  {
    id: 'kcl-sealions',
    advertiser: KCL,
    headline: 'Sea lions can lounge for hours.',
    body: 'Your reports shouldn’t take that long. KCL makes business more efficient.',
    cta: 'Speed things up',
    url: KCL_URL,
    image: '/ads/kcl-sealions.jpg',
    imageBrief: 'Sea lions sprawled across coastal rocks.',
    scene: g('#0f172a', '#0e7490', '#a5f3fc'),
  },
  // ---- El Tiquetero ------------------------------------------------------------------
  {
    id: 'tiq-sunset',
    advertiser: TIQ,
    headline: 'The sun sets at 8:47. The party starts at 9.',
    body: 'Find tonight’s best events across Mallorca with El Tiquetero.',
    cta: 'Find events',
    url: TIQ_URL,
    image: '/ads/tiq-sunset.jpg',
    imageBrief: 'Sun setting into the sea from a quiet beach.',
    scene: g('#4c0519', '#e11d48', '#fdba74'),
  },
  {
    id: 'tiq-moves',
    advertiser: TIQ,
    headline: 'Your dance moves deserve an audience.',
    body: 'Club nights, live music and festivals across Mallorca – all in one place.',
    cta: 'Get tickets',
    url: TIQ_URL,
    image: '/ads/tiq-moves.jpg',
    imageBrief: 'Rock concert in intense stage light.',
    scene: g('#2e1065', '#c026d3', '#22d3ee', 135),
  },
  {
    id: 'tiq-organisers',
    advertiser: TIQ,
    headline: 'Organising an event? Keep the fun – and the money.',
    body: 'List free. No setup costs, no monthly fees, payments straight to your PayPal.',
    cta: 'List your event',
    url: TIQ_URL,
    image: '/ads/tiq-organisers.jpg',
    imageBrief: 'Smoke and light beams over a festival crowd.',
    scene: g('#0f172a', '#4338ca', '#a5b4fc'),
  },
  {
    id: 'tiq-yoga',
    advertiser: TIQ,
    headline: 'Downward dog. Upward mood.',
    body: 'Yoga, workshops and wellness events all over the island.',
    cta: 'Find a class',
    url: TIQ_URL,
    image: '/ads/tiq-yoga.jpg',
    imageBrief: 'Yoga handstand on a sunny beach.',
    scene: g('#0c4a6e', '#f472b6', '#fde68a'),
  },
  {
    id: 'tiq-humans',
    advertiser: TIQ,
    headline: 'Real people, not bots.',
    body: 'Tickets from humans who actually go to the parties.',
    cta: 'Meet the Ticket Guy',
    url: TIQ_URL,
    image: '/ads/tiq-humans.jpg',
    imageBrief: 'Busy Mallorcan beach (Cala Contesa) in summer.',
    scene: g('#7c2d12', '#f97316', '#fef08a', 160),
  },
  {
    id: 'tiq-refresh',
    advertiser: TIQ,
    headline: 'Stop refreshing. Start dancing.',
    body: 'Tickets land in your inbox in seconds.',
    cta: 'Buy tickets',
    url: TIQ_URL,
    image: '/ads/tiq-refresh.jpg',
    imageBrief: 'Phone and coffee on a café table outdoors.',
    scene: g('#1e1b4b', '#7c3aed', '#f0abfc'),
  },
  {
    id: 'tiq-beaches',
    advertiser: TIQ,
    headline: 'Mallorca has hundreds of beaches.',
    body: 'And at least one party worth going to tonight.',
    cta: 'See what’s on',
    url: TIQ_URL,
    image: '/ads/tiq-beaches.jpg',
    imageBrief: 'Cala Contesa, Mallorca, through the pines.',
    scene: g('#155e75', '#06b6d4', '#fef3c7'),
  },
  {
    id: 'tiq-sofa',
    advertiser: TIQ,
    headline: 'Your sofa will forgive you.',
    body: 'Live music in Palma this weekend.',
    cta: 'Get out there',
    url: TIQ_URL,
    image: '/ads/tiq-sofa.jpg',
    imageBrief: 'Band playing on a stage lit in blue.',
    scene: g('#1c1917', '#b45309', '#fcd34d'),
  },
  {
    id: 'tiq-affiliates',
    advertiser: TIQ,
    headline: 'Got followers? Get paid.',
    body: 'Share events and earn commission with the El Tiquetero affiliate programme.',
    cta: 'Become an affiliate',
    url: TIQ_URL,
    image: '/ads/tiq-affiliates.jpg',
    imageBrief: 'Phone glowing with colourful bokeh.',
    scene: g('#831843', '#db2777', '#fbcfe8'),
  },
  {
    id: 'tiq-fomo',
    advertiser: TIQ,
    headline: 'FOMO is a choice.',
    body: 'So is dancing until sunrise.',
    cta: 'Choose dancing',
    url: TIQ_URL,
    image: '/ads/tiq-fomo.jpg',
    imageBrief: 'Campfire on the beach at sunset.',
    scene: g('#312e81', '#ec4899', '#fdba74'),
  },
  {
    id: 'tiq-outfit',
    advertiser: TIQ,
    headline: 'Ticket? Sorted. Outfit? That’s on you.',
    body: 'Fast, easy tickets for every event in Mallorca.',
    cta: 'Get your ticket',
    url: TIQ_URL,
    image: '/ads/tiq-outfit.jpg',
    imageBrief: 'Flamenco dancers in full skirts on stage.',
    scene: g('#4a044e', '#a21caf', '#f5d0fe'),
  },
  {
    id: 'tiq-palma',
    advertiser: TIQ,
    headline: 'The Ticket Guy never sleeps.',
    body: 'Neither does Palma in August.',
    cta: 'Find tonight’s party',
    url: TIQ_URL,
    image: '/ads/tiq-palma.jpg',
    imageBrief: 'Gothic cathedral facade in Palma (black and white).',
    scene: g('#020617', '#1e3a8a', '#fbbf24'),
  },
];

/** Gradient for server ads that don't have a photo yet. */
export function sceneFor(index: number): string {
  return HOUSE_ADS[index % HOUSE_ADS.length]!.scene;
}
